import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import { DesktopDropdownContent, DesktopDropdownRadioItem } from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdex/ui/components/popover';
import { SegmentedControl, SegmentedControlItem } from '@crowdex/ui/components/segmented-control';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crowdex/ui/components/tooltip';
import { DESKTOP_MENU_SURFACE } from '@crowdex/ui/lib/desktop-styles';
import { cn } from '@crowdex/ui/lib/utils';
import {
  Check,
  ChevronDown,
  CircleDashed,
  Cloud,
  Folder,
  FolderPlus,
  GitBranch,
  Laptop,
  LoaderCircle,
  Search,
  Settings,
} from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { PopoverLabel } from '@/components/desktop-config-controls';
import { PickerRow } from '@/components/desktop-shell/composer-menu-list';
import { useComposerMenuSide } from '@/components/desktop-shell/composer-menu-side';
import { repositoryLabel } from '@/components/desktop-summary/summary-workspace-section';
import type { ExecutionMode } from '@/components/worktree-controls';
import { SETUP_CHIP_CLASS } from '@/components/worktree-controls';
import type { RepositoryOrigin } from '@/lib/desktop-shell/repository-options';
import type { SidebarThread } from '@/lib/sidebar-threads';

/** A repository a new conversation can run in: its sidebar name, its folder and where it is. */
export type WhereRepository = { name: string; path: string; origin?: RepositoryOrigin };

/** How a forge repository only the cloud has gets a folder of this computer. */
export type LinkRepositoryHow = 'folder' | 'clone';

/** A forge repository only the cloud has, which a folder of this computer can join. */
function linkable(repository: WhereRepository | undefined): boolean {
  const origin = repository?.origin;
  return (
    origin !== undefined &&
    !origin.onThisComputer &&
    origin.forge !== null &&
    origin.logicalWorkspaceId !== null
  );
}

/**
 * The cloud's empty folder (`/workspace`): no repository, in the cloud. It is no entry of its
 * own: "No repository" with Cloud selects it.
 */
function isEmptySandbox(path: string | null): boolean {
  return path !== null && repositoryLabel(path) === 'Empty sandbox';
}

const segments = (path: string) => path.split(/[\\/]/).filter(Boolean);

/**
 * The end of a folder's path that no other folder's path ends with (`…/Projects/crowdex`
 * beside `…/code/crowdex`): two folders of one name, told apart in their row.
 */
export function pathTail(path: string, others: readonly string[]): string {
  const parts = segments(path);
  const otherParts = others.map(segments);
  for (let count = 2; count < parts.length; count += 1) {
    const tail = parts.slice(-count).join('/');
    if (otherParts.every((other) => other.slice(-count).join('/') !== tail)) return `…/${tail}`;
  }
  return path;
}

/** A repository with when a conversation of it was last active (`null`: never). */
export type RecentRepository = WhereRepository & { usedAt: number | null };

/** Preview fixtures give their age in hours rather than a time. */
const asTime = (updatedAt: number, now: number) =>
  updatedAt < 1_000_000_000_000 ? now - updatedAt * 3_600_000 : updatedAt;

/** The repositories, the ones used last first (by their conversations), then by name. */
export function recentRepositories(
  repositories: readonly WhereRepository[],
  threads: readonly SidebarThread[],
  now = Date.now(),
): RecentRepository[] {
  const used = new Map<string, number>();
  for (const thread of threads) {
    const at = asTime(thread.updatedAt, now);
    used.set(thread.project, Math.max(used.get(thread.project) ?? 0, at));
  }
  return repositories
    .map((repository) => ({ ...repository, usedAt: used.get(repository.name) ?? null }))
    .sort(
      (left, right) =>
        (right.usedAt ?? 0) - (left.usedAt ?? 0) || left.name.localeCompare(right.name),
    );
}

/** `Used 2 min ago`, `Used yesterday`, `Used 3 days ago`. */
export function usedLabel(usedAt: number | null, now = Date.now()): string | null {
  if (usedAt === null) return null;
  const minutes = Math.max(0, Math.floor((now - usedAt) / 60_000));
  if (minutes < 1) return 'Used just now';
  if (minutes < 60) return `Used ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Used ${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Used yesterday';
  return `Used ${days} days ago`;
}

/**
 * Everything "Where it runs" shows and changes. The branch field comes built (it reads the
 * conversation's Git state): the phone's sheet lays it out with the repositories and where it
 * runs; the desktop has it as a chip beside the repository's. The agent is the + menu's.
 */
export type WhereItRunsProps = {
  repositories: readonly WhereRepository[];
  threads: readonly SidebarThread[];
  /** The repository the conversation runs in (its folder). */
  currentPath: string | null;
  runtimeMode: ExecutionMode;
  /** This computer can run it (the desktop app). */
  localAvailable: boolean;
  /** The folder is a Git repository: the conversation can get its own worktree. */
  worktreeAvailable: boolean;
  disabled: boolean;
  /** A cloud sandbox already runs for this conversation: its repository and place stay. */
  locked?: boolean;
  /** Its worktree is being made. */
  pending?: boolean;
  onSelectRepository: (path: string) => void;
  onAddRepository: () => void;
  /** Gives a forge repository only the cloud has a folder of this computer (the desktop app). */
  onLinkRepository?: ((repository: WhereRepository, how: LinkRepositoryHow) => void) | undefined;
  /** A conversation without a repository (the personal folder), when there is one. */
  noRepository?: { selected: boolean; onSelect: () => void } | undefined;
  onChangeMode: (mode: ExecutionMode) => void;
  /** The branch it starts from, as a chip (a row of the phone's sheet); none without a repository. */
  startFrom?: ((variant: 'field' | 'chip') => ReactNode) | undefined;
  /** The worktree's repository setup, when it gets one. */
  environment?: { name: string | null; onOpen: () => void } | undefined;
};

/** Under the branch: what the agent works on. */
function startFromHint(mode: ExecutionMode): string {
  return mode === 'local'
    ? 'The agent works in your checkout'
    : 'A new branch is created for the task';
}

function runsInLabel(mode: ExecutionMode): string {
  return mode === 'cloud' ? 'Cloud sandbox' : 'This computer';
}

const NO_REPOSITORY = 'No repository';
const ICON = 'size-4 shrink-0 text-muted-foreground';

/**
 * "No repository": the personal folder on this computer, the cloud's empty sandbox in the cloud
 * (a conversation in the sandbox shows it selected). `null` when neither exists.
 */
function noRepositoryChoice({
  repositories,
  currentPath,
  runtimeMode,
  noRepository,
  onSelectRepository,
}: Pick<
  WhereItRunsProps,
  'repositories' | 'currentPath' | 'runtimeMode' | 'noRepository' | 'onSelectRepository'
>): { selected: boolean; select: () => void } | null {
  const sandbox = repositories.find((repository) => isEmptySandbox(repository.path));
  if (noRepository === undefined && sandbox === undefined) return null;
  const selected = noRepository?.selected === true || isEmptySandbox(currentPath);
  return {
    selected,
    select: () => {
      if (selected) return;
      if (sandbox !== undefined && (runtimeMode === 'cloud' || noRepository === undefined))
        onSelectRepository(sandbox.path);
      else noRepository?.onSelect();
    },
  };
}

type ListRow =
  | { kind: 'none'; key: string }
  | { kind: 'repository'; key: string; repository: RecentRepository }
  | { kind: 'add'; key: string };

/**
 * The repositories, as the composer's other menus list things: a search field, one 32 px row
 * each (recents first, "No repository" on top), a check on the chosen one. ↑ ↓ move from the
 * search field, ↵ picks. A repository this computer lacks says so and links a folder.
 */
function RepositoryList({
  repositories,
  threads,
  currentPath,
  runtimeMode,
  disabled,
  noRepository,
  localAvailable,
  onLinkRepository,
  onSelectRepository,
  onAdd,
  touch = false,
}: Pick<
  WhereItRunsProps,
  | 'repositories'
  | 'threads'
  | 'currentPath'
  | 'runtimeMode'
  | 'disabled'
  | 'noRepository'
  | 'localAvailable'
  | 'onLinkRepository'
  | 'onSelectRepository'
> & {
  onAdd: () => void;
  /** A finger's 44 px rows (the phone's sheet). */
  touch?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState<number | null>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const now = Date.now();
  const normalized = query.trim().toLocaleLowerCase();
  const none = noRepositoryChoice({
    repositories,
    currentPath,
    runtimeMode,
    noRepository,
    onSelectRepository,
  });
  const listed = recentRepositories(
    repositories.filter((repository) => !isEmptySandbox(repository.path)),
    threads,
    now,
  ).filter((repository) => repository.name.toLocaleLowerCase().includes(normalized));
  const rows: ListRow[] = [
    ...(none !== null && NO_REPOSITORY.toLocaleLowerCase().includes(normalized)
      ? [{ kind: 'none' as const, key: 'none' }]
      : []),
    ...listed.map((repository) => ({
      kind: 'repository' as const,
      key: repository.path,
      repository,
    })),
    { kind: 'add', key: 'add' },
  ];
  const activeIndex = active === null ? null : Math.min(active, rows.length - 1);

  useEffect(() => {
    if (activeIndex !== null) rowRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  function selected(row: ListRow): boolean {
    if (row.kind === 'none') return none?.selected === true;
    return row.kind === 'repository' && row.repository.path === currentPath;
  }

  function pick(row: ListRow): void {
    if (row.kind === 'add') onAdd();
    else if (disabled) return;
    else if (row.kind === 'none') none?.select();
    else onSelectRepository(row.repository.path);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const from = activeIndex ?? (step === 1 ? -1 : rows.length);
      setActive((from + step + rows.length) % rows.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const row = rows[activeIndex ?? (normalized === '' ? -1 : 0)];
      if (row !== undefined) pick(row);
      return;
    }
    // Esc closes the menu and Tab leaves the field; the other keys are the field's alone.
    if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
  }

  const row = (item: ListRow, index: number): ReactNode => {
    const common = {
      active: index === activeIndex,
      onMouseMove: () => {
        if (activeIndex !== null && activeIndex !== index) setActive(index);
      },
      ref: (element: HTMLButtonElement | null) => {
        rowRefs.current[index] = element;
      },
      touch,
    };
    if (item.kind === 'add')
      return (
        <PickerRow
          {...common}
          icon={<FolderPlus aria-hidden className={ICON} />}
          key={item.key}
          label="Add a repository"
          onClick={() => pick(item)}
        />
      );
    const isSelected = selected(item);
    const check = isSelected ? <Check aria-hidden className="size-4 text-foreground" /> : null;
    if (item.kind === 'none')
      return (
        <li key={item.key}>
          <PickerRow
            {...common}
            aria-pressed={isSelected}
            disabled={disabled}
            icon={<CircleDashed aria-hidden className={ICON} />}
            label={NO_REPOSITORY}
            onClick={() => pick(item)}
            trailing={check}
          />
        </li>
      );
    const { repository } = item;
    // Two folders of one name: the end of their paths tells them apart, first in the line.
    const twins = repositories
      .filter((other) => other.name === repository.name && other.path !== repository.path)
      .map((other) => other.path);
    const away = localAvailable && repository.origin?.onThisComputer === false;
    const link = away && onLinkRepository !== undefined && linkable(repository);
    const description = [
      twins.length > 0 ? pathTail(repository.path, twins) : null,
      away ? 'Not on this computer' : null,
      usedLabel(repository.usedAt, now),
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <li className="relative" key={item.key}>
        <PickerRow
          {...common}
          aria-pressed={isSelected}
          className={cn(link && (isSelected ? 'pr-32' : 'pr-26'))}
          description={description}
          disabled={disabled}
          icon={<Folder aria-hidden className={ICON} />}
          label={repository.name}
          onClick={() => pick(item)}
          title={twins.length > 0 ? repository.path : repository.origin?.forge?.fullName}
          trailing={check}
        />
        {link && (
          <button
            aria-label={`Link ${repository.name} to a folder of this computer`}
            className={cn(
              'type-caption absolute top-1/2 h-6 -translate-y-1/2 rounded-md px-2 font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:outline-none disabled:opacity-50',
              isSelected ? 'right-8' : 'right-1.5',
            )}
            disabled={disabled}
            onClick={() => onLinkRepository?.(repository, 'folder')}
            type="button"
          >
            Link a folder
          </button>
        )}
      </li>
    );
  };

  const addIndex = rows.length - 1;
  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="flex h-9 shrink-0 items-center gap-2 border-hairline border-b px-3">
        <Search aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          aria-label="Search repositories"
          className="type-ui h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(event.target.value.trim() === '' ? null : 0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search repositories"
          value={query}
        />
      </div>
      <div className="min-h-0 overflow-y-auto p-1">
        <ul aria-label="Repositories" className="flex flex-col">
          {rows.slice(0, addIndex).map((item, index) => row(item, index))}
        </ul>
        {row(rows[addIndex] as ListRow, addIndex)}
      </div>
    </div>
  );
}

/** A segment of "Runs in", disabled with why in a tooltip. */
function RunsInSegment({
  value,
  icon,
  label,
  reason,
}: {
  value: 'local' | 'cloud';
  icon: ReactNode;
  label: string;
  reason: string | undefined;
}) {
  const reasonId = useId();
  const item = (
    <SegmentedControlItem
      aria-describedby={reason === undefined ? undefined : reasonId}
      className="gap-1.5"
      disabled={reason !== undefined || undefined}
      value={value}
    >
      {icon}
      {label}
    </SegmentedControlItem>
  );
  if (reason === undefined) return item;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex min-w-0 flex-1">
          {item}
          <span className="sr-only" id={reasonId}>
            {reason}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Where it runs, under the repositories: This computer or Cloud, one segmented control. A choice
 * that cannot run (no desktop app, a repository this computer lacks) is disabled with why.
 */
function RunsInChoice({
  runtimeMode,
  localAvailable,
  disabled,
  locked = false,
  repositories,
  currentPath,
  noRepository,
  onChangeMode,
}: Pick<
  WhereItRunsProps,
  | 'runtimeMode'
  | 'localAvailable'
  | 'disabled'
  | 'locked'
  | 'repositories'
  | 'currentPath'
  | 'noRepository'
  | 'onChangeMode'
>) {
  const current = repositories.find((repository) => repository.path === currentPath);
  const localReason = locked
    ? 'Its cloud sandbox is ready: start a new task to run elsewhere'
    : !localAvailable
      ? 'Needs the desktop app'
      : current?.origin?.onThisComputer === false
        ? 'Not on this computer: link a folder first'
        : isEmptySandbox(currentPath) && noRepository === undefined
          ? 'No folder on this computer'
          : undefined;
  const value = runtimeMode === 'cloud' ? 'cloud' : 'local';
  return (
    <TooltipProvider delayDuration={300}>
      <SegmentedControl
        aria-label="Runs in"
        className="mx-1 mb-1 flex"
        disabled={disabled}
        onValueChange={(next) => {
          if (next !== value) onChangeMode(next === 'cloud' ? 'cloud' : 'local');
        }}
        value={value}
      >
        <RunsInSegment
          icon={<Laptop aria-hidden />}
          label="This computer"
          reason={localReason}
          value="local"
        />
        <RunsInSegment
          icon={<Cloud aria-hidden />}
          label="Cloud"
          reason={undefined}
          value="cloud"
        />
      </SegmentedControl>
    </TooltipProvider>
  );
}

/** "New worktree", a checkbox row: on this computer, in a repository. */
function WorktreeRow({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <label
      className={cn(
        'type-ui flex h-8 items-center gap-2.5 rounded-md px-2 text-foreground transition-colors',
        disabled ? 'opacity-50' : 'hover:bg-accent/60',
      )}
      htmlFor={id}
    >
      <Checkbox checked={checked} disabled={disabled} id={id} onCheckedChange={onChange} />
      New worktree
    </label>
  );
}

/** The repository chip's label and icon: where the conversation runs. */
function RepositoryChipContent({
  name,
  runtimeMode,
  pending = false,
}: {
  name: string;
  runtimeMode: ExecutionMode;
  pending?: boolean | undefined;
}) {
  if (pending)
    return (
      <>
        <LoaderCircle aria-hidden className="size-3.5 shrink-0 animate-spin" />
        <span className="min-w-0 truncate">Creating worktree…</span>
      </>
    );
  return (
    <>
      {runtimeMode === 'cloud' ? (
        <Cloud aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <Laptop aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 truncate">{name}</span>
      <ChevronDown aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
    </>
  );
}

/**
 * D20: the repository chip opens where the conversation runs, in one column like the composer's
 * other menus: the repositories (search, "No repository", recents first, add one), then where it
 * runs (This computer or Cloud) and, on this computer in a repository, a new worktree. The branch
 * it starts from is its own chip beside it (the agent is the + menu's). Every choice applies at
 * once and stays for the next conversation; Esc or a click outside closes it.
 */
export function WhereItRunsPopover(
  props: WhereItRunsProps & {
    repositoryName: string;
    /** The chips row around its own chip (the popover opens under the chip). */
    rowClassName: string;
    leading?: ReactNode;
    trailing?: ReactNode;
  },
) {
  const [open, setOpen] = useState(false);
  const menuSide = useComposerMenuSide();
  const disabled = props.disabled || props.locked === true;
  const hasRepository =
    props.currentPath !== null &&
    props.noRepository?.selected !== true &&
    !isEmptySandbox(props.currentPath);
  const here = props.runtimeMode !== 'cloud';
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <fieldset aria-label="New conversation" className={props.rowClassName}>
        {props.leading}
        <PopoverTrigger asChild>
          <button
            aria-label="Repository"
            className={SETUP_CHIP_CLASS}
            disabled={props.disabled}
            title="Where it runs"
            type="button"
          >
            <RepositoryChipContent
              name={props.repositoryName}
              pending={props.pending}
              runtimeMode={props.runtimeMode}
            />
          </button>
        </PopoverTrigger>
        {props.trailing}
      </fieldset>
      <PopoverContent
        align="start"
        aria-label="Where it runs"
        className={cn(
          DESKTOP_MENU_SURFACE,
          'flex max-h-(--radix-popover-content-available-height) w-80 max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden p-0',
        )}
        collisionPadding={12}
        role="dialog"
        side={menuSide}
        sideOffset={8}
      >
        <RepositoryList
          currentPath={props.currentPath}
          disabled={disabled}
          localAvailable={props.localAvailable}
          noRepository={props.noRepository}
          onAdd={() => {
            setOpen(false);
            props.onAddRepository();
          }}
          onLinkRepository={
            props.onLinkRepository === undefined
              ? undefined
              : (repository, how) => {
                  setOpen(false);
                  props.onLinkRepository?.(repository, how);
                }
          }
          onSelectRepository={props.onSelectRepository}
          repositories={props.repositories}
          runtimeMode={props.runtimeMode}
          threads={props.threads}
        />
        <div className="flex shrink-0 flex-col border-hairline border-t p-1">
          <PopoverLabel>Runs in</PopoverLabel>
          <RunsInChoice {...props} disabled={props.disabled} />
          {here && props.localAvailable && props.worktreeAvailable && hasRepository && (
            <WorktreeRow
              checked={props.runtimeMode === 'worktree'}
              disabled={disabled}
              onChange={(checked) => props.onChangeMode(checked ? 'worktree' : 'local')}
            />
          )}
          {props.environment !== undefined && (
            <PickerRow
              active={false}
              description={props.environment.name ?? undefined}
              icon={<Settings aria-hidden className={ICON} />}
              label="Repository setup"
              onClick={props.environment.onOpen}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Mobile 17: where a new conversation runs, in one sheet titled "Where it runs": the
 * repositories (recents first), then the branch it starts from (with a repository) and where it
 * runs (a phone has no chips for them; the agent is the + sheet's).
 */
export function WhereItRunsSheet(props: WhereItRunsProps & { repositoryName: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <>
      <button
        aria-expanded={open}
        aria-label="Repository"
        className={SETUP_CHIP_CLASS}
        disabled={props.disabled}
        onClick={() => setOpen(true)}
        type="button"
      >
        <RepositoryChipContent
          name={props.repositoryName}
          pending={props.pending}
          runtimeMode={props.runtimeMode}
        />
      </button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-modal text-left">
            <button
              aria-label="Close"
              className="absolute inset-0 bg-overlay-backdrop"
              onClick={() => setOpen(false)}
              type="button"
            />
            <div
              aria-label="Where it runs"
              aria-modal
              className="absolute inset-x-0 bottom-0 flex max-h-[92dvh] flex-col gap-4 rounded-t-window bg-popover px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]"
              data-sheet
              role="dialog"
            >
              <span
                aria-hidden
                className="mx-auto h-1 w-10 shrink-0 rounded-full bg-muted-foreground/40"
              />
              <header className="relative flex items-center justify-center">
                <button
                  aria-label="Close"
                  className="absolute left-0 flex size-9 items-center justify-center rounded-control text-muted-foreground active:bg-control"
                  onClick={() => setOpen(false)}
                  type="button"
                >
                  <span aria-hidden className="text-xl leading-none">
                    ×
                  </span>
                </button>
                <h2 className="type-card-title">Where it runs</h2>
              </header>
              {/* The lists scroll; Done stays in reach. */}
              <div className="-mx-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4">
                <section className="flex flex-col gap-2">
                  <h3 className="type-meta px-1 text-muted-foreground">Repository</h3>
                  <div className="rounded-2xl bg-card p-1.5">
                    <RepositoryList
                      currentPath={props.currentPath}
                      disabled={props.disabled || props.locked === true}
                      localAvailable={props.localAvailable}
                      noRepository={props.noRepository}
                      onAdd={() => {
                        setOpen(false);
                        props.onAddRepository();
                      }}
                      onLinkRepository={
                        props.onLinkRepository === undefined
                          ? undefined
                          : (repository, how) => {
                              setOpen(false);
                              props.onLinkRepository?.(repository, how);
                            }
                      }
                      onSelectRepository={props.onSelectRepository}
                      repositories={props.repositories}
                      runtimeMode={props.runtimeMode}
                      threads={props.threads}
                      touch
                    />
                  </div>
                </section>
                <section className="flex flex-col gap-2">
                  <h3 className="type-meta px-1 text-muted-foreground">How it runs</h3>
                  <div className="flex flex-col rounded-2xl bg-card">
                    {props.startFrom !== undefined && (
                      <SheetRow icon={<GitBranch className="size-5" />} label="Start from">
                        {props.startFrom('chip')}
                      </SheetRow>
                    )}
                    <SheetRow icon={<Cloud className="size-5" />} label="Runs in" last>
                      <RunsInMenu {...props} disabled={props.disabled || props.locked === true} />
                    </SheetRow>
                  </div>
                  {props.startFrom !== undefined && (
                    <p className="type-meta px-1 text-muted-foreground">
                      {startFromHint(props.runtimeMode)}
                    </p>
                  )}
                </section>
              </div>
              <Button
                className="h-12 shrink-0 rounded-control text-base"
                onClick={() => setOpen(false)}
              >
                Done
              </Button>
            </div>
          </div>,
          document.querySelector('.desktop-shell') ?? document.body,
        )}
    </>
  );
}

/** A row of the phone's sheet: an icon, what it sets, and its control on the right. */
function SheetRow({
  icon,
  label,
  last = false,
  children,
}: {
  icon: ReactNode;
  label: string;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn('flex min-h-14 items-center gap-3 px-4', !last && 'border-hairline border-b')}
    >
      <span aria-hidden className="text-muted-foreground">
        {icon}
      </span>
      <span className="type-body flex-1">{label}</span>
      <span className="flex min-w-0 justify-end">{children}</span>
    </div>
  );
}

/** Where it runs, as one choice of the phone's sheet: the cloud, this computer, a worktree. */
function RunsInMenu({
  runtimeMode,
  localAvailable,
  worktreeAvailable,
  disabled,
  onChangeMode,
}: Pick<
  WhereItRunsProps,
  'runtimeMode' | 'localAvailable' | 'worktreeAvailable' | 'disabled' | 'onChangeMode'
>) {
  const label = runtimeMode === 'worktree' ? 'This computer · worktree' : runsInLabel(runtimeMode);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Runs in"
          className="flex min-w-0 items-center gap-1 text-muted-foreground disabled:opacity-50"
          disabled={disabled}
          type="button"
        >
          <span className="truncate">{label}</span>
          <ChevronDown aria-hidden className="size-4 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="end" className="w-64" side="bottom" sideOffset={6}>
        <DropdownMenuRadioGroup
          onValueChange={(value) => onChangeMode(value as ExecutionMode)}
          value={runtimeMode}
        >
          <DesktopDropdownRadioItem disabled={!localAvailable} value="local">
            This computer
          </DesktopDropdownRadioItem>
          {worktreeAvailable && (
            <DesktopDropdownRadioItem disabled={!localAvailable} value="worktree">
              This computer · new worktree
            </DesktopDropdownRadioItem>
          )}
          <DesktopDropdownRadioItem value="cloud">Cloud sandbox</DesktopDropdownRadioItem>
        </DropdownMenuRadioGroup>
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}
